import React, { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router";
import { Edit, Settings } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Table,
  Paper,
  Typography,
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  CircularProgress,
} from "@mui/material";
import type { CompanyListInfo, JoyfulStoreSet, RegisterCompanyArgs, StoreInfo } from "../types";
import CompanyEditModal from "./CompanyEditModal";
import CompanyDeleteModal from "./CompanyDeleteModal";

const commonTableCellStyles = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  // fontWeight: 500,
  fontSize: 16,
  letterSpacing: "0.01071em",
  display: "table-cell",
  verticalAlign: "inherit",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  textAlign: "center",
  padding: "16px",
  paddingTop: "20px",
  paddingBottom: "20px",
  color: "rgba(0, 0, 0, 0.87)",
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
  maxWidth: "110px",
  borderLeft: "1px solid rgb(224, 224, 224)",
  borderTop: "1px solid rgb(224, 224, 224)",
};

const headerStyles = {
  ...commonTableCellStyles,
  position: "sticky",
  top: 0,
  zIndex: 1,
  fontSize: 16,
  fontWeight: 'bold',
  textAlign: "center",
  backgroundColor: "#fbefb4",
};

type CompanyTableProps = {
  stores: JoyfulStoreSet[];
  total: number;
  companies: CompanyListInfo[];
  isOpenCompanyEdit: boolean;
  isOpenCompanyDelete: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isLoading: boolean;
  isMoreLoading: boolean;
  error: Error | null;
  onChange: (changes: boolean) => void;
  setIsOpenCompanyEdit: Dispatch<SetStateAction<boolean>>;
  setIsOpenCompanyDelete: Dispatch<SetStateAction<boolean>>;
  setCommitStores: Dispatch<SetStateAction<JoyfulStoreSet[]>>;
  setSelectedCompanyCd: Dispatch<SetStateAction<string>>;
  handleUpdate: (params: RegisterCompanyArgs) => Promise<void>;
  handleDelete: () => Promise<void>;
};

/**
 * CompanyTable Component 企業情報テーブル
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
function CompanyTable({
  stores,
  total,
  companies,
  isUpdating,
  isDeleting,
  isLoading,
  isMoreLoading,
  error,
  isOpenCompanyDelete,
  isOpenCompanyEdit,
  onChange,
  setIsOpenCompanyEdit,
  setIsOpenCompanyDelete,
  handleUpdate,
  handleDelete,
  setCommitStores,
  setSelectedCompanyCd,
}: CompanyTableProps) {

  const [companyName, setCompanyName] = useState("");
  const [companyCd, setCompanyCd] = useState("");
  const [centerCd, setCenterCd] = useState("");
  const [localStores, setLocalStores] = useState<StoreInfo[]>([]);
  const navigate = useNavigate();

  const tableHeadElement = () => (
    <TableRow>
      <TableCell sx={headerStyles}>商品設定</TableCell>
      <TableCell sx={headerStyles}>企業編集</TableCell>
      <TableCell sx={headerStyles}>企業コード</TableCell>
      <TableCell sx={headerStyles}>企業名</TableCell>
      <TableCell sx={headerStyles}>センターコード</TableCell>
      <TableCell sx={headerStyles}>削除</TableCell>
    </TableRow>
  );
  const tableBodyElement = () => (
    <TableBody>
      {companies.map((data) => (
        <TableRow key={data.company_cd}>
          <TableCell sx={{...commonTableCellStyles, textAlign: "center"}}>
            <IconButton
              aria-label="setting"
              onClick={() => {
                navigate(
                  `/joyful/product-setting?company_cd=${encodeURIComponent(data.company_cd)}&company_name=${encodeURIComponent(data.company_name)}`,
                );
              }}
            >
              <Settings />
            </IconButton>
          </TableCell>
          <TableCell sx={{...commonTableCellStyles, textAlign: "center"}}>
            <IconButton
              aria-label="edit"
              onClick={() => {
                setIsOpenCompanyEdit(true);
                setCompanyName(data.company_name ?? '');
                setCompanyCd(data.company_cd ?? '');
                setCenterCd(data.center_cd ?? '');
                setLocalStores(data.stores ?? []);
              }}
            >
              <Edit />
            </IconButton>
          </TableCell>
          <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{data.company_cd}</TableCell>
          <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>
            {data.company_name}
          </TableCell>
          <TableCell sx={{ ...commonTableCellStyles, maxWidth: 50 }}>{data.center_cd}</TableCell>
          {/* <TableCellSmall multi >{data.center_cd}</TableCellSmall> */}
          <TableCell sx={{ ...commonTableCellStyles, maxWidth: 30 }}>
            <IconButton
              aria-label="delete"
              onClick={() => {
                setSelectedCompanyCd(data.company_cd)
                setIsOpenCompanyDelete(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <Box >
      {isLoading ? (
        <Box height={330} textAlign="center" alignContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            mt={3}
            display="flex"
            flexDirection="row"
            borderBottom={4}
            borderColor="primary.main"
            justifyContent="flex-start"
          >
            <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%">
              <Box display="flex" flexDirection="row">
                <Box textAlign="left" display="flex" flexDirection="row" alignItems="flex-end">
                  <Typography fontSize={20}>検索結果：</Typography>
                  <Typography fontSize={20}>{total}件</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box mt={2}>
            <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
              <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="mock table">
                <TableHead sx={{ bgcolor: "#fbefb4" }}>{tableHeadElement()}</TableHead>
                {tableBodyElement()}
              </Table>
              {isMoreLoading && (
                <Box py={3} textAlign="center">
                  <CircularProgress />
                </Box>
              )}
            </TableContainer>
          </Box>
          <CompanyEditModal
            open={isOpenCompanyEdit}
            stores={localStores}
            companyName={companyName}
            companyCd={companyCd}
            centerCd={centerCd}
            setCenterCd={setCenterCd}
            isUpdating={isUpdating}
            onUpdate={handleUpdate}
            onClose={() => setIsOpenCompanyEdit(false)}
            companies={companies}
            setCommitStores={setCommitStores}
          />
          <CompanyDeleteModal
            open={isOpenCompanyDelete}
            isDeleting={isDeleting}
            onClose={() => setIsOpenCompanyDelete(false)}
            onConfirm={handleDelete}
          />
        </>
      )}
    </Box>
  );
}

export default React.memo(CompanyTable);
